import {
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalBody,
  Input,
  Spacer,
  Text,
  ModalFooter,
  Button,
} from '@vitvio/design-system';
import { TimezoneSelector } from '@vitvio/time-components';
import { useState } from 'react';
import { graphql, useMutation } from 'react-relay';
import { getLoggerSafe } from '@vitvio/logging';
import type { CreateHospitalModalMutation } from './__generated__/CreateHospitalModalMutation.graphql';

const mutation = graphql`
  mutation CreateHospitalModalMutation($input: CreateHospitalInput!) {
    createHospital(input: $input) {
      id
      name
      timezone
    }
  }
`;

interface Props {
  isOpen: boolean;
  onClose: (shouldReload: boolean) => void;
}

export function CreateHospitalModal({ isOpen, onClose }: Props) {
  const logger = getLoggerSafe();
  const [hospitalName, setHospitalName] = useState('');
  const [timezone, setTimezone] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [commit] = useMutation<CreateHospitalModalMutation>(mutation);

  const handleHospitalInputChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setHospitalName(event.target.value || '');

  async function handleSubmit() {
    if (isSubmitting) {
      return;
    }

    if (!hospitalName || !timezone) {
      return;
    }

    setIsSubmitting(true);

    await new Promise((resolve, reject) =>
      commit({
        variables: {
          input: {
            name: hospitalName,
            timezone: timezone,
          },
        },
        onCompleted: (response, errors) => {
          if (errors) {
            logger?.error('GraphQL errors during hospital creation', { errors });
            setIsSubmitting(false);
            reject(errors);
          } else {
            resolve(response);
            setHospitalName('');
            setTimezone('');
            setIsSubmitting(false);
            onClose(true);
          }
        },
        onError: (error) => {
          logger?.error(error as Error, { context: 'createHospital', hospitalName, timezone });
          setIsSubmitting(false);
          reject(error);
        },
      }),
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={() => onClose(false)} size={'xl'}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Create Hospital</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Input placeholder="Hospital name" value={hospitalName} onChange={handleHospitalInputChange} />
          <Spacer h={5} />
          <Text>Timezone</Text>
          <TimezoneSelector value={timezone} onChange={setTimezone} />
        </ModalBody>
        <ModalFooter>
          <Button onClick={handleSubmit}>Create Hospital</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
